import { HandleNegativeOnHand } from "@redotech/crdb-enums/inventory/handle-negative-on-hand";
import { ImsStatus } from "@redotech/crdb-enums/inventory/ims-status";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoToggle } from "@redotech/redo-web/arbiter-components/toggle/redo-toggle";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import { Text } from "@redotech/redo-web/text";
import { groupInput, input, InputSchema } from "@redotech/ui/form";
import classNames from "classnames";
import { memo, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import {
  anyInventoryPushEnabled,
  type InventoryPushChannelStatus,
  InventoryPushStatus,
} from "./inventory-push-status";

function formatElapsedTime(startTime: Date): string {
  const now = new Date();
  const elapsedMs = now.getTime() - new Date(startTime).getTime();
  const totalSeconds = Math.floor(elapsedMs / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}h ${minutes}m ${seconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${seconds}s`;
  }
  return `${seconds}s`;
}

export const imsGeneralForm = groupInput({
  enabled: input<boolean>(),
  syncEnabled: input<boolean>(),
  catalogEnabled: input<boolean>(),
  catalogSourceOfTruthStatus: input<ImsStatus>(),
  forecastingEnabled: input<boolean>(),
  status: input<ImsStatus>(),
  imsBillingEnabled: input<boolean>(),
  imsBillingRenewOnPeriodEnd: input<boolean>(),
  imsBillingPriceInCents: input<number>(),
  imsBillingPeriod: input<string>(),
  forecastingBillingEnabled: input<boolean>(),
  forecastingCustomPriceInCents: input<number>(),
  forecastingBillingPeriod: input<string>(),
  orderSyncTemporal: input<boolean>(),
  handleNegativeOnHand: input<HandleNegativeOnHand | null>(),
});

export type ImsGeneralForm = InputSchema.Input<typeof imsGeneralForm>;

export type ImsGeneralValue = InputSchema.Value<typeof imsGeneralForm>;

export const imsGeneralDefault: ImsGeneralValue = {
  enabled: false,
  syncEnabled: false,
  catalogEnabled: false,
  catalogSourceOfTruthStatus: ImsStatus.ReadOnly,
  forecastingEnabled: false,
  status: ImsStatus.ReadOnly,
  imsBillingEnabled: false,
  imsBillingRenewOnPeriodEnd: true,
  imsBillingPriceInCents: 50_000,
  imsBillingPeriod: "monthly",
  forecastingBillingEnabled: false,
  forecastingCustomPriceInCents: 50_000,
  forecastingBillingPeriod: "monthly",
  orderSyncTemporal: false,
  handleNegativeOnHand: null,
};

export const ImsGeneralCard = memo(function ImsGeneralCard({
  input,
  initialStatus,
  onSourceOfTruthToggleChange,
  onOpenForecastingBreakdown,
  imsBillingStartDate,
  selfEnroll,
  inventoryPushChannels,
}: {
  input: ImsGeneralForm;
  inventoryPushChannels: readonly InventoryPushChannelStatus[];
  initialStatus: ImsStatus;
  onSourceOfTruthToggleChange: (value: boolean) => void;
  onOpenForecastingBreakdown?: () => void;
  /** If set, IMS billing has started and the enabled toggle should be disabled */
  imsBillingStartDate?: Date | null;
  /** Whether the customer self-enrolled */
  selfEnroll?: boolean;
}) {
  const params = useParams();
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const teamId = params.teamId!;
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);

  const {
    enabled,
    syncEnabled,
    forecastingEnabled,
    status,
    imsBillingEnabled,
    imsBillingRenewOnPeriodEnd,
    imsBillingPriceInCents,
    imsBillingPeriod,
    forecastingBillingEnabled,
    forecastingCustomPriceInCents,
    forecastingBillingPeriod,
    handleNegativeOnHand,
  } = input.inputs;

  const [forecastStatusVersion, setForecastStatusVersion] = useState(0);
  const [triggerError, setTriggerError] = useState<string | null>(null);
  const [isTriggering, setIsTriggering] = useState(false);
  const [elapsedTime, setElapsedTime] = useState<string | null>(null);

  const forecastStatus = useLoad(
    () => rpcClient.getForecastStatus({ teamId }),
    [rpcClient, teamId, forecastStatusVersion],
  );

  // Update elapsed time every second when a forecast is running
  useEffect(() => {
    if (
      !forecastStatus.pending &&
      !forecastStatus.error &&
      forecastStatus.value?.isForecasting &&
      forecastStatus.value?.workflowStartTime
    ) {
      const updateElapsedTime = () => {
        setElapsedTime(
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          formatElapsedTime(forecastStatus.value!.workflowStartTime!),
        );
      };
      updateElapsedTime();
      const interval = setInterval(updateElapsedTime, 1000);
      return () => clearInterval(interval);
    }
    setElapsedTime(null);
    return undefined;
  }, [forecastStatus.pending, forecastStatus.error, forecastStatus.value]);

  // Poll for status updates while a forecast is running
  useEffect(() => {
    if (
      !forecastStatus.pending &&
      !forecastStatus.error &&
      forecastStatus.value?.isForecasting
    ) {
      const pollInterval = setInterval(() => {
        setForecastStatusVersion((v) => v + 1);
      }, 30_000); // Poll every 30 seconds
      return () => clearInterval(pollInterval);
    }
    return undefined;
  }, [
    forecastStatus.pending,
    forecastStatus.error,
    forecastStatus.value?.isForecasting,
  ]);

  const handleTriggerForecast = async () => {
    setIsTriggering(true);
    setTriggerError(null);
    try {
      const result = await rpcClient.triggerForecast({ teamId });
      if (!result.success && result.error) {
        setTriggerError(result.error);
      } else {
        // Refresh status to show the running workflow
        setForecastStatusVersion((v) => v + 1);
      }
    } catch (error) {
      setTriggerError(error instanceof Error ? error.message : "Unknown error");
    } finally {
      setIsTriggering(false);
    }
  };

  const getStatusLabel = (status: ImsStatus) => {
    switch (status) {
      case ImsStatus.ReadOnly:
        return "Read Only (Default)";
      case ImsStatus.Pending:
        return "Pending Activation";
      case ImsStatus.SourceOfTruth:
        return "Full Source of Truth";
      default:
        return "Unknown";
    }
  };

  const activating =
    initialStatus === ImsStatus.ReadOnly && status.value === ImsStatus.Pending;
  const pushBlocksActivation =
    initialStatus === ImsStatus.ReadOnly &&
    anyInventoryPushEnabled(inventoryPushChannels);

  const handleImsEnabledChange = (value: boolean) => {
    if (value && !syncEnabled.value) {
      // If trying to enable IMS, sync must be enabled first
      return;
    }
    enabled.setValue(value);
    // IMS now requires source of truth, so enabling IMS also turns on source
    // of truth, which prompts for confirmation on save.
    if (initialStatus === ImsStatus.ReadOnly) {
      onSourceOfTruthToggleChange(value);
    }
  };

  const handleSourceOfTruthChange = (value: boolean) => {
    onSourceOfTruthToggleChange(value);
    if (!value) {
      enabled.setValue(false);
    }
  };

  const handleSyncEnabledChange = (value: boolean) => {
    if (!value) {
      // If disabling sync, also disable IMS, forecasting, and billing
      enabled.setValue(false);
      if (initialStatus === ImsStatus.ReadOnly) {
        onSourceOfTruthToggleChange(false);
      }
      forecastingEnabled.setValue(false);
      imsBillingEnabled.setValue(false);
      forecastingBillingEnabled.setValue(false);
    }
    syncEnabled.setValue(value);
  };

  return (
    <Card title="Inventory Management System">
      <section className={gridCss.grid}>
        <div className={gridCss.span12}>
          <div className={gridCss.grid}>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description="Enable product and inventory sync from Shopify"
                label="Sync Enabled"
                setValue={handleSyncEnabledChange}
                size="sm"
                value={syncEnabled.value ?? false}
              />
            </div>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <Flex dir="column" gap="sm">
                <RedoToggle
                  description={
                    !syncEnabled.value
                      ? "Enable sync first to use full IMS functionality"
                      : pushBlocksActivation && !enabled.value
                        ? "Turn off inventory push for all sales channels first"
                        : "Enable full Inventory Management System functionality"
                  }
                  disabled={
                    !syncEnabled.value ||
                    (pushBlocksActivation && !enabled.value)
                  }
                  label="IMS Enabled"
                  setValue={handleImsEnabledChange}
                  size="sm"
                  value={enabled.value ?? false}
                />
                <Text fontSize="xs" textColor="warning">
                  Turning on IMS now also requires turning on Source of Truth.
                  If the merchant only needs bill of materials, turn on Catalog
                  Enabled in Product Catalog instead.
                </Text>
              </Flex>
            </div>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description={
                  forecastingEnabled.value &&
                  !forecastStatus.pending &&
                  !forecastStatus.error &&
                  forecastStatus.value?.lastForecastAt
                    ? `Enable sales forecasting for products (Last forecast: ${new Date(
                        forecastStatus.value.lastForecastAt,
                      ).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric",
                      })})`
                    : "Enable sales forecasting for products"
                }
                disabled={!enabled.value}
                label="Forecasting Enabled"
                setValue={(value) => forecastingEnabled.setValue(value)}
                size="sm"
                value={forecastingEnabled.value ?? false}
              />
            </div>
            {forecastingEnabled.value && (
              <div
                className={classNames(gridCss.span6L, gridCss.span12)}
                style={{
                  backgroundColor: "var(--redo-colors-background-bg-tertiary)",
                  borderRadius: "var(--border-radius-lg)",
                  padding: "var(--redo-spacing-xl)",
                }}
              >
                <Flex dir="column" gap="md">
                  <Flex align="center" gap="md">
                    <RedoButton
                      disabled={
                        isTriggering ||
                        (forecastStatus.value?.isForecasting ?? false) ||
                        forecastStatus.value?.hasSnowflakeData === false
                      }
                      onClick={handleTriggerForecast}
                      pending={isTriggering}
                      size="sm"
                      text={
                        forecastStatus.value?.isForecasting
                          ? "Forecast Running..."
                          : "Run Forecast"
                      }
                      theme="normal"
                    />
                    {forecastStatus.value?.hasSnowflakeData === false && (
                      <Text fontSize="sm" textColor="secondary">
                        Data syncing into Snowflake. Should be ready in ~1 hour
                      </Text>
                    )}
                    {forecastStatus.value?.isForecasting && elapsedTime && (
                      <Text fontSize="sm" textColor="secondary">
                        Running for {elapsedTime}
                      </Text>
                    )}
                  </Flex>
                  {triggerError && (
                    <Text fontSize="xs" textColor="error">
                      {triggerError}
                    </Text>
                  )}
                </Flex>
              </div>
            )}
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description="Count up an order's shortfall; pre-existing negatives stay negative."
                disabled={!enabled.value}
                label="Correct Negative On-Hand"
                setValue={(value) =>
                  handleNegativeOnHand.setValue(
                    value ? HandleNegativeOnHand.CountCorrection : null,
                  )
                }
                size="sm"
                value={
                  handleNegativeOnHand.value ===
                  HandleNegativeOnHand.CountCorrection
                }
              />
            </div>
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <RedoToggle
                description={
                  pushBlocksActivation && !activating
                    ? "Turn off inventory push for all sales channels first"
                    : getStatusLabel(status.value || ImsStatus.ReadOnly)
                }
                disabled={
                  !enabled.value ||
                  initialStatus !== ImsStatus.ReadOnly ||
                  (pushBlocksActivation && !activating)
                }
                label="Source of Truth"
                setValue={handleSourceOfTruthChange}
                size="sm"
                value={
                  status.value === ImsStatus.SourceOfTruth ||
                  status.value === ImsStatus.Pending
                }
              />
            </div>
            {/* design-allow: grid cell matching the sibling toggle cells */}
            <div className={classNames(gridCss.span6L, gridCss.span12)}>
              <InventoryPushStatus channels={inventoryPushChannels} />
            </div>
            {/* Billing Section */}
            <div className={gridCss.span12}>
              <Flex align="baseline" gap="sm">
                <Text
                  fontSize="md"
                  fontWeight="semibold"
                  style={{
                    marginTop: "var(--redo-spacing-3xl)",
                    marginBottom: "var(--redo-spacing-xl)",
                  }}
                >
                  Billing
                </Text>
                {selfEnroll && (
                  <Text
                    fontSize="xs"
                    style={{
                      marginTop: "var(--redo-spacing-3xl)",
                      marginBottom: "var(--redo-spacing-xl)",
                    }}
                    textColor="secondary"
                  >
                    (self-enrolled)
                  </Text>
                )}
              </Flex>
            </div>
            <div
              className={classNames(gridCss.span6L, gridCss.span12)}
              style={{
                backgroundColor: "var(--redo-colors-background-bg-tertiary)",
                borderRadius: "var(--border-radius-lg)",
                padding: "var(--redo-spacing-xl)",
              }}
            >
              <Flex dir="column" gap="md">
                <RedoToggle
                  description={
                    imsBillingStartDate
                      ? "Billing has started. To cancel, disable auto-renew instead."
                      : "Enable IMS subscription billing"
                  }
                  disabled={!!imsBillingStartDate}
                  label="IMS Subscription"
                  setValue={(value) => imsBillingEnabled.setValue(value)}
                  size="sm"
                  value={imsBillingEnabled.value ?? false}
                />
                {imsBillingEnabled.value && (
                  <>
                    <RedoToggle
                      description="Automatically renew subscription at the end of each billing period"
                      label="Auto-renew"
                      setValue={(value) =>
                        imsBillingRenewOnPeriodEnd.setValue(value)
                      }
                      size="sm"
                      value={imsBillingRenewOnPeriodEnd.value ?? true}
                    />
                    <Flex align="center" gap="md">
                      <Flex align="baseline" gap="xs">
                        <Text fontSize="sm" fontWeight="semibold">
                          Price
                        </Text>
                        <Text fontSize="xs" textColor="secondary">
                          (
                          {imsBillingPeriod.value === "yearly"
                            ? "annual"
                            : "monthly"}
                          )
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold">
                          :
                        </Text>
                      </Flex>
                      <Flex align="center" gap="xs">
                        <Text fontSize="sm">$</Text>
                        <RedoTextInput
                          setValue={(value: string) => {
                            const cents = Math.round(Number(value) * 100);
                            imsBillingPriceInCents.setValue(
                              isNaN(cents) ? 0 : cents,
                            );
                          }}
                          size="sm"
                          value={(
                            (imsBillingPriceInCents.value ?? 0) / 100
                          ).toFixed(2)}
                        />
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>
            </div>
            <div
              className={classNames(gridCss.span6L, gridCss.span12)}
              style={{
                backgroundColor: "var(--redo-colors-background-bg-tertiary)",
                borderRadius: "var(--border-radius-lg)",
                padding: "var(--redo-spacing-xl)",
              }}
            >
              <Flex dir="column" gap="md">
                <RedoToggle
                  description="Billed as a line on the IMS bill, on the IMS plan's cadence. Turning it off drops the line from the next bill."
                  label="Forecasting add-on"
                  setValue={(value) =>
                    forecastingBillingEnabled.setValue(value)
                  }
                  size="sm"
                  value={forecastingBillingEnabled.value ?? false}
                />
                {forecastingBillingEnabled.value && (
                  <>
                    <Flex align="center" gap="md">
                      <Flex align="baseline" gap="xs">
                        <Text fontSize="sm" fontWeight="semibold">
                          Price
                        </Text>
                        <Text fontSize="xs" textColor="secondary">
                          (
                          {(imsBillingEnabled.value
                            ? imsBillingPeriod.value
                            : forecastingBillingPeriod.value) === "yearly"
                            ? "annual"
                            : "monthly"}
                          )
                        </Text>
                        <Text fontSize="sm" fontWeight="semibold">
                          :
                        </Text>
                      </Flex>
                      <Flex align="center" gap="xs">
                        <Text fontSize="sm">$</Text>
                        <RedoTextInput
                          setValue={(value: string) => {
                            const cents = Math.round(Number(value) * 100);
                            forecastingCustomPriceInCents.setValue(
                              isNaN(cents) ? 0 : cents,
                            );
                          }}
                          size="sm"
                          value={(
                            (forecastingCustomPriceInCents.value ?? 0) / 100
                          ).toFixed(2)}
                        />
                      </Flex>
                    </Flex>
                    {onOpenForecastingBreakdown && (
                      <RedoButton
                        hierarchy="secondary"
                        onClick={onOpenForecastingBreakdown}
                        size="xs"
                        text="View breakdown"
                        theme="normal"
                      />
                    )}
                  </>
                )}
              </Flex>
            </div>
          </div>
        </div>
      </section>
    </Card>
  );
});
